/**
 * Uitklapmenu binnen de pane houden (ST14, besluit Asil 23 oktober 2026, #2752).
 *
 * Het ⋯-menu staat rechts uitgelijnd onder zijn knop met een minimale breedte. In een smalle pane (320 px)
 * viel het menu links buiten de pane of buiten een afknippende kaart ("n de PDF" in plaats van "Toon in de
 * PDF"). Bij openen meten we de ruimte die het menu heeft (de pane, verkleind tot elke ouder die zijn
 * inhoud afknipt), begrenzen de breedte op die ruimte en schuiven het menu zo dat het erin valt.
 * Zuiver: getest in de engine-tests; useKeepInPane.ts doet het meten.
 */

/** Ruimte tussen menu en rand, gelijk aan de halve marge in `max-width: calc(100vw - 16px)`. */
export const MENU_EDGE_MARGIN = 8;

export interface SpanX {
  left: number;
  right: number;
}

/** Doorsnede van twee horizontale intervallen. */
export function intersectX(a: SpanX, b: SpanX): SpanX {
  return { left: Math.max(a.left, b.left), right: Math.min(a.right, b.right) };
}

/** Maximale breedte (px) van het menu binnen de ruimte, met een marge aan beide kanten. */
export function menuMaxWidth(bound: SpanX, margin: number = MENU_EDGE_MARGIN): number {
  return Math.max(0, Math.floor(bound.right - bound.left - 2 * margin));
}

/**
 * Horizontale verschuiving (px) zodat het menu binnen de ruimte valt; positief = naar rechts. Past het
 * menu niet, dan wint de linkerrand (het begin van de tekst blijft zichtbaar).
 */
export function menuShiftX(menu: SpanX, bound: SpanX, margin: number = MENU_EDGE_MARGIN): number {
  const minLeft = bound.left + margin;
  const maxRight = bound.right - margin;
  let dx = 0;
  if (menu.right > maxRight) dx = maxRight - menu.right;
  if (menu.left + dx < minLeft) dx = minLeft - menu.left;
  return Math.round(dx);
}

/** Leesbare maximale breedte van een menuvenster (W2, #2730); daaronder vult het de pane met marge. */
export const PANE_MENU_READABLE_MAX = 760;
/** Marge (px) per kant tussen een pane-breed menuvenster en de rand van de pane (#2730). */
export const PANE_MENU_EDGE = 16;

/**
 * Plaats van een menuvenster in een balk (of pane) van `barWidth` px (#2826, besluit Asil 25 oktober 2026):
 * elk menu, ook een lijstmenu, vult de breedte met 16 px marge per kant tot 760 px en staat dan gecentreerd.
 * Nooit smaller dan 200 px; is de pane smaller, dan houdt het venster minstens 8 px links.
 */
export function popoverPlacement(barWidth: number): { left: number; width: number } {
  const usable = Math.max(200, barWidth - 2 * PANE_MENU_EDGE);
  const width = Math.min(PANE_MENU_READABLE_MAX, usable);
  return { left: Math.max(8, (barWidth - width) / 2), width };
}
